import type {
  MetaResponseModel,
  SearchResponseModel,
  SearchRequestModelFilter,
} from '@searchspring/snapi-types'

import { uuidv4 } from '../utils/uuidv4'
import { getCookie } from '../utils/get_cookie'
import { UrlManager } from '../helpers/url_manager'
import { SearchspringUiRenderer } from '../helpers/searchspring_ui_renderer'
import { SearchspringProduct } from '../types/searchspring'
import BaseSearchSpringController from './base_searchspring_controller'

export default class CollectionController extends BaseSearchSpringController {
  static targets = ['products', 'filters']
  static values = {
    handle: String,
    productsPerPage: { type: Number, default: 30 },
  }

  declare handleValue: string
  declare productsPerPageValue: number
  declare readonly productsTarget: HTMLElement
  declare readonly filtersTarget: HTMLElement
  declare readonly hasProductsTarget: boolean
  declare readonly hasFiltersTarget: boolean

  private pageLoadId!: string
  private userId!: string
  private sessionId!: string

  connect() {
    this.pageLoadId = uuidv4()
    this.userId = getCookie('_ss_sui') || uuidv4()
    this.sessionId = getCookie('_ss_sni') || uuidv4()

    this.performSearch()

    // Handle browser back/forward buttons
    window.addEventListener('popstate', this.onPopState.bind(this))
  }

  disconnect() {
    window.removeEventListener('popstate', this.onPopState.bind(this))
  }

  onPopState() {
    this.performSearch()
  }

  async performSearch() {
    const handle = this.handleValue

    if (!handle) {
      console.error('[CollectionController] No collection handle provided')
      return
    }

    // Parse path filters (legacy Shopify/Hydrogen structure)
    // /collections/:handle/:filters
    const pathSegments = window.location.pathname.split('/')
    const collectionIndex = pathSegments.indexOf('collections')
    let pathFilters: string[] = []

    if (collectionIndex > -1 && pathSegments.length > collectionIndex + 2) {
      const filterSegment = pathSegments[collectionIndex + 2]
      if (filterSegment) {
        pathFilters = filterSegment
          .split('+')
          .map((f) => f.trim().replace(/-|\s/g, ''))
      }
    }

    // Logic for "New Arrivals" hack
    const searchParams = new URL(window.location.href).searchParams
    const isNewArrivalsPage = searchParams.get('ss_less_than_90_days') === '1'

    // Construct Filters
    const searchFilters: SearchRequestModelFilter[] = []

    // 1. Collection Handle
    searchFilters.push({
      type: 'value',
      field: 'collection_handle',
      value: handle,
      background: true,
    } as SearchRequestModelFilter)

    // 2. Filters from URL Path (ss_tags)
    pathFilters.forEach((tag) => {
      searchFilters.push({
        type: 'value',
        field: 'ss_tags',
        value: tag,
        background: true,
      } as SearchRequestModelFilter)
    })

    // 3. New Arrivals Logic
    if (isNewArrivalsPage) {
      searchFilters.push({
        type: 'value',
        field: 'ss_tags',
        value: 'salecollectionpage',
        background: true,
        exclude: true,
      } as SearchRequestModelFilter)
    }

    // 4. Facet Filters from Query Params
    const queryFilters = UrlManager.getFiltersFromUrl(searchParams)
    const ssQueryFilters = UrlManager.convertToSearchspringFilters(queryFilters)
    searchFilters.push(...ssQueryFilters)

    const productsPerPage = this.productsPerPageValue

    const searchRequest = {
      query: {
        string: searchParams.get('q') || '',
      },
      filters: searchFilters,
      pagination: {
        pageSize: productsPerPage,
      },
      tracking: {
        userId: this.userId,
        sessionId: this.sessionId,
        pageLoadId: this.pageLoadId,
      },
    }

    try {
      const [, response]: [MetaResponseModel, SearchResponseModel] =
        await this.client.search({
          ...searchRequest,
          filters: searchFilters,
        })

      if (response) {
        if (response.merchandising?.redirect) {
          window.location.href = response.merchandising.redirect
          return
        }

        this.render(response)
      }
    } catch (error) {
      console.error('Searchspring Error:', error)
      this.productsTarget.innerHTML = '<p>Error loading products.</p>'
    }
  }

  render(response: SearchResponseModel) {
    if (this.hasProductsTarget) {
      this.productsTarget.innerHTML = SearchspringUiRenderer.renderProducts(
        (response.results as unknown as SearchspringProduct[]) || [],
      )
    }

    if (this.hasFiltersTarget) {
      this.filtersTarget.innerHTML = SearchspringUiRenderer.renderFacets(
        response.facets || [],
      )
    }
  }

  handleFilterClick(event: Event) {
    event.preventDefault()
    const link = event.currentTarget as HTMLAnchorElement
    const url = link.href

    // Update URL state
    window.history.pushState({}, '', url)

    // Trigger new search
    this.performSearch()

    // Scroll to top of collection
    this.element.scrollIntoView({ behavior: 'smooth' })
  }
}
